import { useState, useCallback, useRef, useEffect } from 'react';

import { getIdsWithNoParentsInSameList } from '@hh.ru/magritte-ui-tree-selector/collection/treeCollectionHelper';
import { AdditionalDefault } from '@hh.ru/magritte-ui-tree-selector/collection/types';
import ImmutableSelectionStrategy from '@hh.ru/magritte-ui-tree-selector/strategy/immutableSelectionStrategy';
import { TreeSelectorProps } from '@hh.ru/magritte-ui-tree-selector/types';

type UseSelectedHookProps<Additional extends AdditionalDefault> = {
    strategy: ImmutableSelectionStrategy<Additional>;
} & Pick<TreeSelectorProps<Additional>, 'value' | 'onChange' | 'collapseToParentId' | 'maxSelected' | 'collection'>;

type UseSelectedHookReturn = {
    selected: string[];
    setSelected: (id: string, isSelected: boolean) => void;
};

interface UseSelectedHook {
    <Additional extends AdditionalDefault>(props: UseSelectedHookProps<Additional>): UseSelectedHookReturn;
}

export const useSelected: UseSelectedHook = ({
    value,
    maxSelected,
    strategy,
    collection,
    collapseToParentId,
    onChange,
}) => {
    const getSelectedFromValue = useCallback((): string[] => {
        let newValue = value.slice();
        if (maxSelected && newValue && newValue.length > maxSelected) {
            newValue = newValue.slice(0, maxSelected);
        }
        return strategy.add([], newValue);
    }, [value, maxSelected, strategy]);

    const [selected, setSelectedState] = useState(getSelectedFromValue);
    const firstRender = useRef(true);

    const setSelected = useCallback(
        (id: string, isSelected: boolean) => {
            const ids = [id];
            const updatedSelected = isSelected ? strategy.add(selected, ids) : strategy.remove(selected, ids);
            const filteredUpdatedSelected = collapseToParentId
                ? getIdsWithNoParentsInSameList(collection, updatedSelected)
                : updatedSelected;
            if (maxSelected && filteredUpdatedSelected.length > maxSelected) {
                filteredUpdatedSelected.length = maxSelected;
            }

            onChange(filteredUpdatedSelected, id, isSelected);
        },
        [strategy, selected, collection, collapseToParentId, onChange, maxSelected]
    );

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }

        const selected = getSelectedFromValue();

        setSelectedState(selected);
    }, [getSelectedFromValue, firstRender]);

    return {
        selected,
        setSelected,
    };
};
